import React, { useCallback, useEffect, useState } from 'react';
import { Navigation } from '../components/Navigation.tsx';
import { HostsTable } from '../components/HostsTable.tsx';
import { HostDetail } from '../components/HostDetail.tsx';
import { HostAudit } from '../components/HostAudit.tsx';
import { HostPipelineTab } from '../components/HostPipelineTab.tsx';
import hostService, { HostWithStats, HostAuditSummary, HostDoc } from '../services/hostService.ts';
import './HostsDashboard.css';

type AuditFilterKey = '' | 'missingGeo' | 'missingCover' | 'svgCover' | 'missingDesc' | 'missingUrl';

export const HostsDashboard: React.FC = () => {
  const [hosts, setHosts] = useState<HostWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [postCountsLoading, setPostCountsLoading] = useState(false);
  const [auditSummary, setAuditSummary] = useState<HostAuditSummary>({
    totalHosts: 0, missingGeo: 0, missingCover: 0, svgCover: 0, missingDescription: 0, missingUrl: 0,
  });
  const [selectedHost, setSelectedHost] = useState<HostWithStats | null>(null);
  const [unmappedCandidates, setUnmappedCandidates] = useState<{ place: string; count: number; source: string; sampleEventUrl: string }[]>([]);
  const [unmappedLoading, setUnmappedLoading] = useState(false);
  const [auditFilter, setAuditFilter] = useState<AuditFilterKey>('');
  const [activeTab, setActiveTab] = useState<'table' | 'audit' | 'pipeline'>('table');
  const [error, setError] = useState<string | null>(null);

  const loadHosts = useCallback(async () => {
    setError(null);
    setLoading(true);
    setPostCountsLoading(false);

    let rawHosts: HostDoc[];
    try {
      rawHosts = await hostService.getAllHosts();
    } catch (err: any) {
      console.error('Error loading hosts:', err);
      const msg =
        err?.code && err?.message
          ? `${err.code}: ${err.message}`
          : err?.message || String(err) || 'Failed to load hosts';
      setError(msg);
      setHosts([]);
      setAuditSummary({
        totalHosts: 0,
        missingGeo: 0,
        missingCover: 0,
        svgCover: 0,
        missingDescription: 0,
        missingUrl: 0,
      });
      setLoading(false);
      return;
    }

    const hostIds = rawHosts.map((h) => h.id);
    const enrichedNoCounts = hostService.enrichHostsWithStats(rawHosts, {});
    setHosts(enrichedNoCounts);
    setAuditSummary(hostService.computeAuditSummary(enrichedNoCounts));
    setLoading(false);

    setPostCountsLoading(true);
    try {
      const postCounts = await hostService.getLinkedPostCounts(hostIds);
      const enriched = hostService.enrichHostsWithStats(rawHosts, postCounts);
      setHosts(enriched);
      setAuditSummary(hostService.computeAuditSummary(enriched));
    } catch (err: any) {
      console.error('Error loading post counts (hosts are still shown):', err);
    } finally {
      setPostCountsLoading(false);
    }
  }, []);

  const loadUnmapped = useCallback(async () => {
    if (hosts.length === 0) return;
    setUnmappedLoading(true);
    try {
      const hostTitles = new Set(hosts.map((h) => h.title).filter(Boolean));
      const candidates = await hostService.getUnmappedPlaceCandidates(hostTitles, 500);
      setUnmappedCandidates(candidates);
    } catch (err) {
      console.error('Error loading unmapped candidates:', err);
    } finally {
      setUnmappedLoading(false);
    }
  }, [hosts]);

  useEffect(() => {
    loadHosts();
  }, [loadHosts]);

  useEffect(() => {
    setSelectedHost((prev) => {
      if (!prev) return null;
      return hosts.find((h) => h.id === prev.id) || prev;
    });
  }, [hosts]);

  useEffect(() => {
    if (activeTab === 'audit' && hosts.length > 0 && unmappedCandidates.length === 0 && !unmappedLoading) {
      loadUnmapped();
    }
  }, [activeTab, hosts, unmappedCandidates.length, unmappedLoading, loadUnmapped]);

  const handleFilterAudit = (filter: AuditFilterKey) => {
    setAuditFilter(filter);
    if (filter) {
      setActiveTab('table');
    }
  };

  const focusHostById = useCallback(
    (hostId: string) => {
      const h = hosts.find((x) => x.id === hostId);
      if (h) {
        setSelectedHost(h);
        setActiveTab('table');
      }
    },
    [hosts],
  );

  return (
    <div className="hosts-dashboard">
      <Navigation />
      <div className="hosts-dashboard-header">
        <div className="hosts-dashboard-title-row">
          <h1>Hosts Dashboard</h1>
          <button className="hosts-refresh-btn" onClick={loadHosts} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
        <div className="hosts-tab-nav">
          <button
            className={`hosts-tab-btn ${activeTab === 'table' ? 'active' : ''}`}
            onClick={() => setActiveTab('table')}
          >
            Hosts Table
          </button>
          <button
            className={`hosts-tab-btn ${activeTab === 'audit' ? 'active' : ''}`}
            onClick={() => setActiveTab('audit')}
          >
            Audit &amp; Candidates
          </button>
          <button
            className={`hosts-tab-btn ${activeTab === 'pipeline' ? 'active' : ''}`}
            onClick={() => setActiveTab('pipeline')}
          >
            Pipeline
          </button>
        </div>
      </div>

      {error && (
        <div className="hosts-error-banner">
          <strong>Error:</strong> {error}
          <button onClick={loadHosts} className="hosts-retry-btn">Retry</button>
        </div>
      )}

      <div className="hosts-dashboard-content">
        {activeTab === 'table' && (
          <div className="hosts-table-and-detail">
            <div className={`hosts-table-pane ${selectedHost ? 'with-detail' : ''}`}>
              <HostsTable
                hosts={hosts}
                loading={loading}
                postCountsLoading={postCountsLoading}
                onSelectHost={(h) => setSelectedHost(h)}
                selectedHostId={selectedHost?.id}
                externalAuditFilter={auditFilter}
                onClearAuditFilter={() => setAuditFilter('')}
              />
            </div>
            {selectedHost && (
              <div className="hosts-detail-pane">
                <HostDetail host={selectedHost} onClose={() => setSelectedHost(null)} />
              </div>
            )}
          </div>
        )}

        {activeTab === 'audit' && (
          <HostAudit
            summary={auditSummary}
            loading={loading}
            unmappedCandidates={unmappedCandidates}
            unmappedLoading={unmappedLoading}
            onFilterAudit={handleFilterAudit}
          />
        )}

        {activeTab === 'pipeline' && (
          <HostPipelineTab selectedHost={selectedHost} onSelectHostById={focusHostById} />
        )}
      </div>
    </div>
  );
};

export default HostsDashboard;
